import { Link } from "react-router-dom";
import { Clock, Utensils, Hotel, Bus, Camera, Heart } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCurrency } from "@/context/CurrencyContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function DestinationCard({ p }) {
  const { user, setUser } = useAuth();
  const { format } = useCurrency();
  const wished = user?.wishlist?.includes(p.slug);

  const toggleWish = async (e) => {
    e.preventDefault();
    if (!user) { toast.info("Sign in to save favourites"); return; }
    try {
      if (wished) {
        await api.delete(`/wishlist/${p.slug}`);
        setUser({ ...user, wishlist: user.wishlist.filter((s) => s !== p.slug) });
      } else {
        await api.post(`/wishlist/${p.slug}`);
        setUser({ ...user, wishlist: [...(user.wishlist || []), p.slug] });
      }
    } catch {}
  };

  return (
    <article
      data-testid={`destination-card-${p.slug}`}
      className="group relative rounded-3xl overflow-hidden card-lift bg-white dark:bg-slate-900 border border-slate-200/70 dark:border-white/10 shadow-xl shadow-blue-900/5"
    >
      <div className="relative h-72 overflow-hidden">
        <img loading="lazy" src={p.image} alt={p.title}
          className="w-full h-full object-cover group-hover:scale-110" style={{transition:"transform 1s ease"}} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
        <button
          data-testid={`wishlist-${p.slug}`}
          onClick={toggleWish}
          className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center glass ${wished ? "text-red-500" : "text-white"}`}
          aria-label="Wishlist"
        >
          <Heart className="w-4 h-4" fill={wished ? "currentColor" : "none"} />
        </button>
        <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-[#D4AF37] text-[10px] uppercase tracking-widest text-blue-950 font-bold">
          {p.category}
        </div>
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-white text-2xl font-bold">{p.title}</h3>
          <p className="text-white/80 text-sm">{p.location}</p>
        </div>
      </div>
      <div className="p-6">
        <div className="flex flex-wrap gap-2 text-xs mb-4">
          {p.hotel && <span className="px-2.5 py-1 rounded-full bg-blue-50 dark:bg-white/10 text-blue-900 dark:text-blue-200 flex items-center gap-1"><Hotel className="w-3 h-3"/>Hotel</span>}
          {p.meals && <span className="px-2.5 py-1 rounded-full bg-amber-50 dark:bg-white/10 text-amber-900 dark:text-amber-200 flex items-center gap-1"><Utensils className="w-3 h-3"/>Meals</span>}
          {p.transport && <span className="px-2.5 py-1 rounded-full bg-emerald-50 dark:bg-white/10 text-emerald-900 dark:text-emerald-200 flex items-center gap-1"><Bus className="w-3 h-3"/>Transport</span>}
          {p.sightseeing && <span className="px-2.5 py-1 rounded-full bg-rose-50 dark:bg-white/10 text-rose-900 dark:text-rose-200 flex items-center gap-1"><Camera className="w-3 h-3"/>Sightseeing</span>}
        </div>
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Starting from</div>
            <div className="text-2xl font-bold text-blue-950 dark:text-white">{format(p.price)}<span className="text-xs font-normal text-slate-500"> /person</span></div>
          </div>
          <div className="flex items-center gap-1 text-sm text-slate-600 dark:text-slate-300">
            <Clock className="w-3.5 h-3.5"/> {p.duration}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Link data-testid={`view-details-${p.slug}`} to={`/packages/${p.slug}`}
            className="text-center text-sm px-4 py-2.5 rounded-full btn-outline-gold">View Details</Link>
          <Link data-testid={`book-now-${p.slug}`} to={`/book/${p.slug}`}
            className="text-center text-sm px-4 py-2.5 rounded-full btn-gold">Book Now</Link>
        </div>
      </div>
    </article>
  );
}
